/**
 * goalDetector.js
 *
 * Detects score changes from socket livescore payloads.
 * The result is stored in a dedicated Vuex state (`activeGoalEvents`) — completely
 * decoupled from `livescoreExtraData` so server overwrites cannot clear the badge.
 */

/**
 * Compares the existing event's scoreboard results with the incoming change.
 * Returns the position of the scoring team ('1' = home, '2' = away), or null.
 *
 * Pure detection — does NOT mutate the change payload.
 *
 * @param {Object} existingEvent - Current event object in the store lookup table.
 * @param {Object} change        - Incoming socket change: { id, livescore, ... }
 * @returns {string|null}
 */
export function detectGoalPosition(existingEvent, change) {
    const oldResults = existingEvent?.livescore?.scoreboard?.results;
    const incomingLs = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;
    const newResults = incomingLs?.scoreboard?.results;

    if (!oldResults || !newResults) return null;

    for (const newR of newResults) {
        const pos = String(newR.position ?? newR.Position ?? '');
        if (!pos) continue;
        const oldR = oldResults.find(r => String(r.position ?? r.Position ?? '') === pos);
        if (!oldR) continue;
        const oldVal = Number(oldR.value ?? oldR.Value ?? 0);
        const newVal = Number(newR.value ?? newR.Value ?? 0);
        if (newVal > oldVal) return pos;
    }
    return null;
}

/**
 * @deprecated Use detectGoalPosition + Vuex activeGoalEvents state instead.
 * Kept for modules that still use the injection approach (detailLive, topsports, etc.)
 */
export function injectGoalIfScoreChanged(existingEvent, change) {
    const position = detectGoalPosition(existingEvent, change);
    if (!position) return null;

    const incomingLs = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;
    if (!incomingLs) return null;

    const prev = Array.isArray(incomingLs.livescoreExtraData) ? incomingLs.livescoreExtraData : [];
    incomingLs.livescoreExtraData = [
        ...prev.filter(e => e && e.type !== 9),
        { type: 9, name: 'goal', position },
    ];
    return position;
}
